import Loader from '@/components/Loader';
import Layout from '@/components/ux/Layout';
import PageSearchFormInput from '@/components/ux/PageSearchFormInput';
import { capturePosthogEvent, log, userUtils, utils } from '@/helpers';
import useDebugState from '@/hooks/useDebugState';
import useQuery from '@/hooks/UseQuery';
import { useCampaignBuilderStore, useCampaignsStore, useCampaignsTableStore, useMaximizeStore, useSearchStore, useUserStoresStore } from '@/stores';
import { Button, DropdownFilter, type FilterDef, type FilterResults, Filters, Flex, Tab, Tabs, Typography } from '@alpine-iq/ui-library';
import React from 'react';
import CampaignsTable from './components/CampaignsTable';
import { type FiltersData, getFilterDefs, getFiltersFromURL, updateURL } from './filters';
import LazyWrap from '@/components/LazyWrap';
import { CampaignTabs, CampaignTabType } from './campaigns.page.utils';
import { CampaignChannelType, CampaignFilters } from '@/types';
import CampaignActionButtons from '../campaign-builder/components/CampaignActionButtons';
import { useCampaignTagsStore } from '@/stores/campaigns/useCampaignTagsStore';
import CampaignsMoreButton from './components/CampaignsMoreButton';
import { CoMarketingNeedsAttentionNotificationIcon } from '../coMarketing/components/CoMarketingNeedsAttention';
import { setPageTitle } from '@/hooks/UsePageViews';

const UnlayerEditor = React.lazy(() => import('../campaign-builder/components/unlayer/UnlayerEditor'));
const ColumnSettingsDrawer = React.lazy(() => import('./components/ColumnSettingsDrawer'));
const CampaignBuilderPage = React.lazy(() => import('../campaign-builder/CampaignBuilderPage'));
const CoMarketingPage = React.lazy(() => import('../coMarketing/CoMarketingPage'));
const MessageReportModal = React.lazy(() => import('./components/MessageReportModal'));
const CampaignArchiveModals = React.lazy(() => import('./components/CampaignArchiveModals'));
const CampaignTagDrawer = React.lazy(() => import('../campaign-builder/components/tags/CampaignTagDrawer'));
const CampaignTagRemoveModal = React.lazy(() => import('../campaign-builder/components/tags/CampaignTagRemoveModal'));
const CampaignTagInputModal = React.lazy(() => import('../campaign-builder/components/tags/CampaignTagInputModal'));
const EmptyState = React.lazy(() => import('./components/EmptyState'));
export default function CampaignsPage() {
	const query = useQuery();
	const campaignID = query.get('campaignID');
	const templateID = query.get('templateID');

	const { inputValue, setSearch, setQueryParam } = useSearchStore();
	const { loaded: loadedTags, tags, fetchTags } = useCampaignTagsStore();
	const [filters, setFilters] = useDebugState<Partial<FiltersData>>(getFiltersFromURL(), userUtils.debugMode(), 'Filters', 'pink');

	const { tab, setTab } = useCampaignsTableStore();
	const { loadCampaigns, campaignsMap, setFilters: setCampaignsFilters, ...campaignCtx } = useCampaignsStore();
	const { loaded: loadedStores, getRetailStores } = useUserStoresStore();

	React.useEffect(() => {
		setQueryParam('cmpSearch');
		if (!campaignCtx.loaded) {
			loadCampaigns();
		}
		if (!loadedTags) fetchTags();
		if (!loadedStores) getRetailStores();
		if (campaignID) onSelectCampaign(campaignID, templateID || '');
	}, []);

	const filterDefs: FilterDef[] = React.useMemo(() => {
		return getFilterDefs(tags);
	}, [tags]);

	const handleFilterChange = (newFilters: FilterResults) => {
		if (newFilters.archived === 'false') {
			newFilters.archived = undefined;
		}
		if (newFilters.pointsBoost === 'false') {
			newFilters.pointsBoost = undefined;
		}
		updateURL(newFilters);
		setFilters(newFilters);
		setCampaignsFilters((prev) => {
			const filterData = newFilters as unknown as FiltersData;
			// Gotta convert the FiltersData from the filters format to Query format
			const queryFilters: CampaignFilters = {
				...prev,
				status: filterData.status,
				channels: filterData.channels,
				types: filterData.types,
				endDate: filterData.endDate,
				startDate: filterData.startDate,
				archived: filterData.archived ? 1 : 0,
				pointsBoost: filterData.pointsBoost ? 1 : 0,
				triggers: filterData.triggers,
				tags: filterData.tags,
			};

			return queryFilters;
		});
		capturePosthogEvent('Campaigns - Filters Changed');
	};

	const isSearching = inputValue.length > 0;
	const hasCampaigns = !!Object.keys(campaignsMap).length;
	const showEmptyState = campaignCtx.loaded && !hasCampaigns && !campaignCtx.loading && !isSearching;

	return (
		<>
			<LazyWrap fallback={<></>}>
				<MessageReportModal />
				<CampaignArchiveModals />
				<CampaignTagRemoveModal />
				<CampaignTagInputModal />
				<CampaignTagDrawer />
			</LazyWrap>

			<Layout>
				<Flex
					column
					gap="24"
					padding="24px 40px"
				>
					<Flex
						align="center"
						justify="space-between"
					>
						<Typography variant="h1">Campaigns</Typography>
						<Flex align="center">
							<PageSearchFormInput
								value={inputValue}
								onChange={(value) =>
									setSearch(value.target.value, () => {
										setCampaignsFilters((prev) => {
											return {
												...prev,
												search: value.target.value,
											};
										});
									})
								}
								placeholder="Search"
							/>

							<LazyWrap fallback={<></>}>
								<ColumnSettingsDrawer />
								<CampaignsMoreButton />
							</LazyWrap>

							<Button
								round
								variant="primary"
								icon="ADD"
								onClick={() => onSelectCampaign('-1')}
							/>
						</Flex>
					</Flex>
					<Flex>
						<Tabs>
							{Object.entries(CampaignTabs)
								.filter(([_, info]) => info.isVisible())
								.map(([key, info]) => (
									<Tab
										key={key}
										active={tab === key}
										style={{
											cursor: 'pointer',
											height: 50,
											paddingBottom: 8,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											userSelect: 'none',
											gap: 8,
										}}
										onClick={() => {
											capturePosthogEvent('Campaigns - Tab Selected', {
												campaignID: campaignID,
												tab: key,
											});
											setTab(key as CampaignTabType);
											setPageTitle();
										}}
									>
										{info.label()}
										{['sponsoring', 'installed'].includes(key) && <CoMarketingNeedsAttentionNotificationIcon />}
									</Tab>
								))}
						</Tabs>
					</Flex>
					{tab === 'campaigns' && (
						<>
							<React.Suspense fallback={<Loader />}>
								<React.Suspense fallback={<Loader />}>
									{/*  */}
									{showEmptyState && <EmptyState />}
								</React.Suspense>
								{!!Object.keys(campaignsMap).length && (
									<Flex>
										<Filters
											filterDefs={filterDefs}
											value={filters as FilterResults}
											onChange={(value) => handleFilterChange(value)}
											style={{ paddingBlock: 0 }}
										>
											{filterDefs.map((def) => (
												<DropdownFilter
													key={`${def.label}-${def.type}`}
													filterDef={def}
												/>
											))}
										</Filters>
									</Flex>
								)}
								<React.Suspense fallback={<Loader />}>
									<CampaignsTable filters={filters} />
								</React.Suspense>
							</React.Suspense>
						</>
					)}
					{(tab === 'sponsoring' || tab === 'installed' || tab === 'sponsored') && (
						<React.Suspense fallback={<Loader />}>
							<CoMarketingPage />
						</React.Suspense>
					)}
				</Flex>
			</Layout>
		</>
	);
}

const maximizePage = () => {
	const useMaxStore = useMaximizeStore.getState();
	const hasEmailContentOpen = useMaxStore.maximizedKey === 'email-template-saved-design';
	const hasTextContentOpen = useMaxStore.maximizedKey === 'text-template-saved-design';

	log('maximizing page ----------------------------------------');
	log('hasEmailContentOpen', hasEmailContentOpen);
	log('hasTextContentOpen', hasTextContentOpen);

	useMaxStore.maximize(() => <CampaignBuilderPage />, {
		maximizedKey: 'campaign-builder',
		centerTitle: 'Campaign Builder',
		keepRendered: true,
		extra() {
			return <CampaignActionButtons />;
		},
	});

	if (hasEmailContentOpen) {
		useMaxStore.maximize(() => <UnlayerEditor channel={CampaignChannelType.Email} />, {
			maximizedKey: 'email-template-saved-design',
			centerTitle: 'Email Template',
			confirmExit: false,
			extra() {
				return (
					<>
						<Button
							variant="primary"
							size="medium"
							onClick={() => useMaxStore.unmaximize()}
						>
							Continue
						</Button>
					</>
				);
			},
		});
	}

	if (hasTextContentOpen) {
		useMaxStore.maximize(() => <UnlayerEditor channel={CampaignChannelType.Text} />, {
			maximizedKey: 'text-template-saved-design',
			centerTitle: 'Text Template',
			confirmExit: false,
			extra() {
				return (
					<>
						<Button
							variant="primary"
							size="medium"
							onClick={() => useMaxStore.unmaximize()}
						>
							Continue
						</Button>
					</>
				);
			},
		});
	}
};

export const onSelectCampaign = (campaignID: string, templateID?: string) => {
	log('on select campaign', campaignID, templateID);
	if (!campaignID) return;
	const maxStore = useMaximizeStore.getState();
	const campaignBuilderStore = useCampaignBuilderStore.getState();

	maxStore.trackParam('campaignID');
	maxStore.addUnmaximizeCallback(campaignBuilderStore.reset);
	const url = new URL(window.location.href);
	if (campaignID === '-1') {
		url.searchParams.delete('stage');
		campaignBuilderStore.reset();
	}
	url.searchParams.set('campaignID', campaignID.toString());
	if (templateID) url.searchParams.set('templateID', templateID.toString());
	else url.searchParams.delete('templateID');
	window.history.pushState({}, '', url.toString());
	maximizePage();

	capturePosthogEvent('Campaigns - Campaign Selected', {
		campaignID: campaignID,
		templateID: templateID,
	});
};
